import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Table, Tag, Typography } from 'antd';
import { listSalesTeamTransferHistory, type SalesTeamTransferRow } from '@/services/salesTeam.service';

type TransferHistoryPanelProps = {
  entityType: string;
  entityId: string;
};

export const TransferHistoryPanel: React.FC<TransferHistoryPanelProps> = ({
  entityType,
  entityId,
}) => {
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['sales-team-transfer-history', entityType, entityId],
    queryFn: () => listSalesTeamTransferHistory(entityType, entityId),
    enabled: !!entityType && !!entityId,
  });

  const columns = [
    {
      title: 'Date',
      dataIndex: 'transfer_date',
      key: 'transfer_date',
      width: 160,
      render: (v: string) => (v ? new Date(v).toLocaleString() : '—'),
    },
    {
      title: 'From',
      key: 'from',
      render: (_: unknown, r: SalesTeamTransferRow) =>
        r.from_team_name || r.from_team_id || '—',
    },
    {
      title: 'To',
      key: 'to',
      render: (_: unknown, r: SalesTeamTransferRow) => r.to_team_name || r.to_team_id,
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      ellipsis: true,
    },
    {
      title: 'Downstream',
      dataIndex: 'transfer_downstream',
      key: 'transfer_downstream',
      width: 100,
      render: (v: boolean) => (v ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag>),
    },
  ];

  if (!rows.length && !isLoading) return null;

  return (
    <Card size="small" title="Team transfer history" style={{ marginTop: 16 }}>
      <Table
        size="small"
        rowKey="id"
        loading={isLoading}
        dataSource={rows}
        columns={columns}
        pagination={false}
      />
      {!rows.length && !isLoading ? (
        <Typography.Text type="secondary">No transfers recorded yet.</Typography.Text>
      ) : null}
    </Card>
  );
};
